import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  address: '', city: '', state: '', zipCode: '',
  phone: '', email: '',
  emergencyContact: { name: '', phone: '', relationship: '' },
  insuranceProvider: '', policyNumber: '', groupNumber: '',
  subscriberName: '', subscriberDOB: '',
  currentMedications: '', allergies: '', pastDiagnoses: '', currentPhysician: '',
  previousTreatments: '', currentSymptoms: '', reasonForVisit: '', substanceUse: '',
  familyMentalHealthHistory: '',
  hipaaConsent: false, consentSignature: '',
};

// Validation rules per step
const STEP_VALIDATION = {
  0: (data) => data.firstName && data.lastName && data.dateOfBirth && data.gender && data.phone && data.email,
  1: () => true, // Insurance is optional
  2: () => true, // Medical history is optional
  3: (data) => data.reasonForVisit,
  4: (data) => data.hipaaConsent && data.consentSignature,
};

export const useIntakeForm = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Note: setFormData is stable per React guarantees, no need in deps
  const update = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateEmergencyContact = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [field]: value }
    }));
  }, []);

  // step and formData are correctly in deps
  const canProceed = useCallback(() => {
    const validator = STEP_VALIDATION[step];
    return validator ? validator(formData) : true;
  }, [step, formData]);

  // Note: setStep is stable per React guarantees
  const nextStep = useCallback(() => {
    setStep((s) => s + 1);
  }, []);

  const prevStep = useCallback(() => {
    setStep((s) => s - 1);
  }, []);

  // formData is correctly in deps; API is module-level constant
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/intake`, formData);
      setSubmitted(true);
      toast.success('Intake form submitted successfully!');
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [formData]);

  return {
    step,
    formData,
    submitting,
    submitted,
    update,
    updateEmergencyContact,
    canProceed,
    nextStep,
    prevStep,
    handleSubmit,
  };
};
